import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Palette, Eye, Clock, Sparkles, Mic, Wand2 } from "lucide-react";

/**
 * Bottom sheet exposing composer tools (vibe/whisper/schedule/doodle/voice/tone).
 * Renders only on mobile — desktop keeps the inline row.
 */
export default function MobileToolsSheet({
  open,
  onClose,
  vibe,
  whisper,
  scheduledFor,
  hasText,
  toneLoading,
  onOpenVibe,
  onToggleWhisper,
  onOpenSchedule,
  onOpenDoodle,
  onOpenVoice,
  onOpenTone,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const item = ({ icon: Icon, label, desc, color, active, disabled, testid, onClick }) => (
    <button
      data-testid={testid}
      disabled={disabled}
      onClick={() => { onClick?.(); onClose(); }}
      className={`w-full flex items-center gap-3 border-hard-2 rounded-2xl p-3 text-left press-btn shadow-hard-sm ${disabled ? "opacity-40" : ""} ${active ? "bg-[#CCFF00]" : "bg-white"}`}
    >
      <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center" style={{ background: color }}>
        <Icon strokeWidth={3} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm leading-tight">{label}</div>
        <div className="text-[11px] text-[#555] leading-tight">{desc}</div>
      </div>
      {active && <span className="text-[10px] font-black uppercase tracking-widest bg-[#0A0A0A] text-white rounded-full px-2 py-0.5">AKTİF</span>}
    </button>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50 md:hidden"
          onClick={onClose}
          data-testid="mobile-tools-sheet"
        >
          <motion.div
            key="sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            className="absolute inset-x-0 bottom-0 bg-[#F9F8F6] border-t-[3px] border-[#0A0A0A] rounded-t-3xl p-4 pb-6 max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-4" aria-hidden />

            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Mesaj Araçları</div>
              <button
                data-testid="mobile-tools-close"
                onClick={onClose}
                className="w-9 h-9 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
                aria-label="Kapat"
              >
                <X strokeWidth={3} className="w-4 h-4" />
              </button>
            </div>

            <div className="grid gap-2.5">
              {item({ icon: Palette, label: "Vibe", desc: vibe ? `Seçili: ${vibe}` : "Mesajın moduna renk ver", color: "#CCFF00", active: !!vibe, testid: "mobile-tool-vibe", onClick: onOpenVibe })}
              {item({ icon: Eye, label: "Fısıltı", desc: whisper ? "Aktif — 5sn sonra saklanır" : "Karşı taraf tıklayınca 5sn görünür", color: "#D4B3FF", active: whisper, testid: "mobile-tool-whisper", onClick: onToggleWhisper })}
              {item({ icon: Clock, label: "Zaman kapsülü", desc: scheduledFor ? `Zamanlı: ${new Date(scheduledFor).toLocaleString()}` : "İleri bir tarihe planla", color: "#FF9E80", active: !!scheduledFor, testid: "mobile-tool-schedule", onClick: onOpenSchedule })}
              {item({ icon: Sparkles, label: "Çizim", desc: "Küçük bir doodle gönder", color: "#CEECFF", testid: "mobile-tool-doodle", onClick: onOpenDoodle })}
              {item({ icon: Mic, label: "Sesli not", desc: "30 saniyeye kadar kaydet", color: "#FFF7C2", testid: "mobile-tool-voice", onClick: onOpenVoice })}
              {item({ icon: Wand2, label: "Ton değiştir", desc: hasText ? "Yazdığını AI ile dönüştür" : "Önce mesajını yaz", color: "#FFE7CC", disabled: !hasText || toneLoading, testid: "mobile-tool-tone", onClick: onOpenTone })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
